import React, { useState } from 'react';
import BasePOIModal from './BasePOIModal';
import { Button } from '@common/Button';
import { Input, Select } from '@common/FormFields';
import { MeasurementSelector } from './components/MeasurementSelector';
import { NotesAndPhotos } from './components/NotesAndPhotos';
import { WidthClearance } from './components/WidthClearance';
import { X } from 'lucide-react';
import { Toast } from '@common/Toast';

const SURFACE_TYPES = [
  { value: 'asphalt', label: 'Asphalt' },
  { value: 'concrete', label: 'Concrete' },
  { value: 'gravel_compacted', label: 'Gravel compacted' },
  { value: 'gravel_soft', label: 'Gravel soft' },
  { value: 'sand', label: 'Sand' },
  { value: 'dirt', label: 'Dirt' }
];

const ROAD_TYPES = [
  { value: 'highway', label: 'Highway' },
  { value: 'state_highway', label: 'State Highway' },
  { value: 'toll_road', label: 'Toll Road' },
  { value: 'county_road', label: 'County Roads' },
  { value: 'rural_secondary', label: 'Rural and Secondary Road' },
  { value: 'urban_road', label: 'Urban Road' },
  { value: 'one_way', label: 'One-Way Road' },
  { value: 'temporary', label: 'Temporary Road' },
  { value: 'construction', label: 'Construction Zone' }
];

const LANE_COUNTS = [
  { value: 'single', label: 'Single Lane' },
  { value: 'two_lanes', label: 'Two-Lanes - One per direction' },
  { value: 'four_lanes', label: 'Four-Lanes - two per direction' },
  { value: 'other', label: 'Others - see notes' }
];

export const RoadPOIModal = ({ type, poiInfo, onClose, onSave, onBack }) => {
  const [toast, setToast] = useState(null);
  const [surfaceType, setSurfaceType] = useState('');
  const [roadType, setRoadType] = useState('');
  const [laneCount, setLaneCount] = useState('');
  const [width, setWidth] = useState({ feet: '', inches: '', meters: '' });
  const [touchLevel, setTouchLevel] = useState(null);
  const [measurementType, setMeasurementType] = useState('touch');
  const [customHeight, setCustomHeight] = useState({ feet: '', inches: '', meters: '' });
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    
    try {
      // Required fields validation
      const requiredFields = {
        roadType: 'Road type is required',
        measurements: 'Measurement is required'
      };

      const errors = {};
      if (!roadType) errors.roadType = requiredFields.roadType;
      if (measurementType === 'touch' && !touchLevel) errors.measurements = requiredFields.measurements;
      if (measurementType === 'custom' && !customHeight.feet && !customHeight.meters) {
        errors.measurements = requiredFields.measurements;
      }

      if (Object.keys(errors).length > 0) {
        setError('Please fill in all required fields');
        setLoading(false);
        return false;
      }

      if (!roadType) {
        setToast({
          type: 'error',
          message: 'Please select a road type',
          zIndex: 1000
        });
        setLoading(false);
        return false;
      }

      if (measurementType === 'touch' && !touchLevel) {
        setToast({
          type: 'error',
          message: 'Please select a touch level',
          zIndex: 1000
        });
        setLoading(false);
        return false;
      }

      const measurements = [];
      if (measurementType === 'touch') {
        measurements.push({
          type: 'touch',
          value: touchLevel,
          unit: 'inches'
        });
      } else {
        if (customHeight.meters) {
          measurements.push({
            type: 'custom',
            value: parseFloat(customHeight.meters),
            unit: 'meters'
          });
        } else if (customHeight.feet) {
          measurements.push({
            type: 'custom',
            value: parseInt(customHeight.feet),
            unit: 'feet'
          });
          if (customHeight.inches) {
            measurements.push({
              type: 'custom',
              value: parseInt(customHeight.inches),
              unit: 'inches'
            });
          }
        } else {
          setError('Please enter height measurements');
          setLoading(false);
          return false;
        }
      }

      const result = await onSave({
        id: poiInfo.id,
        coordinates: poiInfo.coordinates,
        address: poiInfo.address,
        measurements,
        notes,
        photos,
        roadDetails: {
          surfaceType: surfaceType || null,
          roadType,
          laneCount: laneCount || null
        }
      });

      if (result.success) {
        setToast({
          type: 'success',
          message: 'POI saved successfully',
          zIndex: 1000
        });
      }
    
      return true;
    } catch (error) {
      setToast({
        type: 'error',
        message: error.message || 'Failed to save measurement',
        zIndex: 1000
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const roadFields = (
    <>
      <MeasurementSelector
        measurementType={measurementType}
        setMeasurementType={setMeasurementType}
        touchLevel={touchLevel}
        setTouchLevel={setTouchLevel}
        customHeight={customHeight}
        setCustomHeight={setCustomHeight}
      />

      <WidthClearance width={width} setWidth={setWidth} />

      <div>
        <label className="block text-gray-400 mb-2">Surface Type</label>
        <select
          value={surfaceType}
          onChange={(e) => setSurfaceType(e.target.value)}
          className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
        >
          <option value="">Select Surface Type</option>
          {SURFACE_TYPES.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-gray-400 mb-2">Road Type</label>
        <select
          value={roadType}
          onChange={(e) => setRoadType(e.target.value)}
          className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
        >
          <option value="">Select Road Type</option>
          {ROAD_TYPES.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-gray-400 mb-2">Number of Lanes</label>
        <select
          value={laneCount}
          onChange={(e) => setLaneCount(e.target.value)}
          className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
        >
          <option value="">Select Lane Count</option>
          {LANE_COUNTS.map(count => (
            <option key={count.value} value={count.value}>{count.label}</option>
          ))}
        </select>
      </div>

      <NotesAndPhotos
        notes={notes}
        setNotes={setNotes}
        photos={photos}
        setPhotos={setPhotos}
        loading={loading}
      />
    </>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center z-[400] p-4 overflow-y-auto">
        <div className="bg-[#2A2B2E] rounded-lg w-full max-w-xl my-8">
          {/* Header */}
          <div className="border-b border-gray-700 p-6">
            <div className="flex items-start justify-between">
              <div className="space-y-3">
                <h2 className="text-xl font-semibold text-white">
                  High Pole Measurement - {type}
                </h2>
                <div className="space-y-1.5">
                  <div className="font-mono text-sm text-blue-400 mb-1">
                    POI ID: {poiInfo.id}
                  </div>
                  {poiInfo.coordinates && (
                    <div className="text-sm text-gray-400">
                      GPS: {Number(poiInfo.coordinates.latitude).toFixed(6)}, {Number(poiInfo.coordinates.longitude).toFixed(6)}
                    </div>
                  )}
                  <div className="text-sm text-gray-400">
                    Address: {poiInfo.address || 'Getting address...'}
                  </div>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {roadFields}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-700 p-6">
            <div className="flex justify-end gap-3">
              {onBack && (
                <Button
                  onClick={onBack}
                  variant="secondary"
                  className="bg-gray-700 hover:bg-gray-600 text-white"
                >
                  Back
                </Button>
              )}
              <Button
                onClick={onClose}
                variant="secondary"
                className="bg-gray-700 hover:bg-gray-600 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                loading={loading}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                Save Measurement
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default RoadPOIModal;